import { Component, computed, DestroyRef, ElementRef, inject, signal, viewChild } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { rxResourceFixed } from '@zayan-erp/shared';
import { ProjectsService } from '../../services/projects.service';
import { TableModule } from '@openng/optimus-ui/table';
import { of } from 'rxjs';
import { ConfirmHelperService } from '@zayan-erp/shared';

@Component({
  selector: 'lib-project-lot-relationships',
  imports: [TableModule],
  templateUrl: './project-lot-relationships.component.html'
})
export class ProjectLotRelationshipsComponent {
  projectId = signal<string | null>(inject(ActivatedRoute).snapshot.params['id'] ?? null);
  #projectsService = inject(ProjectsService);
  #destroyRef = inject(DestroyRef);
  #confirmationService = inject(ConfirmHelperService);

  fileInput = viewChild<ElementRef<HTMLInputElement>>('fileInput');

  lotRelationshipsResource = rxResourceFixed({
    params: () => ({ projectId: this.projectId() }),
    defaultValue: [],
    stream: ({ params }) => {
      if (!params.projectId || params.projectId === 'new') {
        return of([]);
      }
      return this.#projectsService.getProjectLotRelationships(params.projectId);
    }
  });

  lotRelationships = computed(() => this.lotRelationshipsResource.value() ?? []);
  importErrors = signal<string[]>([]);

  onImportClick(): void {
    this.fileInput()?.nativeElement.click();
  }

  getLotNumbers(neighbors: { lotId: string; lotNo: string }[]): string {
    if (!neighbors || neighbors.length === 0) {
      return '-';
    }
    return neighbors.map(lot => lot.lotNo).join(', ');
  }

  onFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];

    if (!file) {
      return;
    }

    const projectId = this.projectId();
    if (!projectId || projectId === 'new') {
      return;
    }

    this.#projectsService
      .importProjectLotRelationships(projectId, file)
      .pipe(takeUntilDestroyed(this.#destroyRef))
      .subscribe({
        next: () => {
          this.importErrors.set([]);
          this.lotRelationshipsResource.reload();
          // Reset file input
          if (input) {
            input.value = '';
          }
        },
        error: err => {
          const errors: string[] = Array.isArray(err?.error?.errors) ? err.error.errors : [];
          this.importErrors.set(errors);
          if (input) {
            input.value = '';
          }
        }
      });
  }

  onExportClick(): void {
    this.#projectsService
      .exportProjectLotRelationships(this.projectId() ?? '')
      .pipe(takeUntilDestroyed(this.#destroyRef))
      .subscribe({
        error: err => {
          const message =
            err?.message ??
            err?.error?.message ??
            (Array.isArray(err?.error?.data) && err?.error?.data?.length > 0 ? String(err.error.data[0]) : null) ??
            'An error occurred while exporting.';
          this.showErrorPopup(message);
        }
      });
  }

  showErrorPopup(message: string): void {
    this.#confirmationService.showModal({
      icon: 'confirm-icon',
      message,
      header: 'Error',
      acceptLabel: 'OK',
      acceptSeverity: 'primary',
      rejectLabel: ''
    });
  }
}
